import React, { useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import {
  Wrench,
  LogOut,
  LayoutDashboard,
  Ticket,
  BarChart3,
  Menu,
  X,
  Bell,
  Building2,
  MapPin,
  Plus,
  MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import NotificationBell from './NotificationBell';

const PortalLayout = () => {
  const { user, logout } = useAuth();
  const { plants, selectedPlant, setSelectedPlant, fetchPlants, locations, selectedLocation, setSelectedLocation, fetchLocations } = useData();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [crossDeptUnread, setCrossDeptUnread] = React.useState(0);

  useEffect(() => {
    fetchPlants();
    fetchLocations();
  }, [fetchPlants, fetchLocations]);

  // Poll cross-dept tasks unread count every 5 minutes
  useEffect(() => {
    const token = localStorage.getItem('maintix_token');
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cross-dept-tasks/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCrossDeptUnread(data.my_received_unread || 0);
        }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 300000);
    return () => clearInterval(interval);
  }, []);

  // Auto-select user's plant/location on mount when they have restricted access
  useEffect(() => {
    if (!plants.length || !user) return;
    const accessPlants = user.plant_access?.length > 0
      ? plants.filter(p => user.plant_access.includes(p.id))
      : [];
    // If user has access to exactly 1 plant, auto-select it
    if (accessPlants.length === 1 && (!selectedPlant || selectedPlant === 'all')) {
      setSelectedPlant(accessPlants[0].id);
    }
  }, [plants, user]);

  useEffect(() => {
    if (!locations.length || !user) return;
    const accessLocations = user.location_access?.length > 0
      ? locations.filter(l => user.location_access.includes(l.id))
      : [];
    // If user has access to exactly 1 location, auto-select it
    if (accessLocations.length === 1 && (!selectedLocation || selectedLocation === 'all')) {
      setSelectedLocation(accessLocations[0].id);
    }
  }, [locations, user, selectedPlant]);

  // Filter plants/locations based on user access
  const userPlants = user?.plant_access?.length > 0 
    ? plants.filter(p => user.plant_access.includes(p.id))
    : plants;
  
  const userLocations = user?.location_access?.length > 0
    ? locations.filter(l => user.location_access.includes(l.id))
    : locations.filter(l => !selectedPlant || selectedPlant === 'all' || l.plant_id === selectedPlant);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/portal', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/portal/tickets', label: 'Tichete', icon: Ticket },
    { path: '/portal/cross-dept-tasks', label: 'Task-uri Interdep.', icon: MessageSquare, badge: crossDeptUnread },
    { path: '/portal/reports', label: 'Rapoarte', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white z-40 shadow-lg">
        <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Wrench className="w-6 h-6" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold">Portal Sef Sectie</h1>
                <p className="text-xs text-blue-200">
                  MAINTIX
                  {userPlants.length === 1 && <span> - {userPlants[0].name}</span>}
                  {userLocations.length === 1 && <span> / {userLocations[0].name}</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {menuItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive ? 'bg-white/20 text-white' : 'text-blue-100 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-rose-500 text-white rounded-full">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Plant/Location Filter - Compact */}
            <div className="hidden md:flex items-center gap-2">
              <Select value={selectedPlant || 'all'} onValueChange={setSelectedPlant}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white text-sm h-9">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Fabrica" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate sediile</SelectItem>
                  {userPlants.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <NotificationBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 hover:bg-white/10">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.full_name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="text-slate-500">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Deconectare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-30 pt-16">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative bg-white shadow-xl max-w-xs w-full h-full">
            {/* Mobile Filters */}
            <div className="p-4 border-b space-y-3">
              <Select value={selectedPlant || 'all'} onValueChange={setSelectedPlant}>
                <SelectTrigger className="w-full">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Fabrica" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate sediile</SelectItem>
                  {userPlants.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedLocation || 'all'} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-full">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Locatie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate locatiile</SelectItem>
                  {userLocations.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mobile Navigation */}
            <nav className="p-4 space-y-1">
              {menuItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium flex-1">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-rose-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Quick Action */}
            <div className="p-4 border-t">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/portal?create=true');
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Raporteaza Problema
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30">
        <div className="flex items-center justify-around py-2">
          {menuItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 px-3 py-1 ${
                  isActive ? 'text-blue-600' : 'text-slate-500'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute -top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold bg-rose-500 text-white rounded-full">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default PortalLayout;
